import {
    EventPostV4DTO,
    KilledEventPostV4DTO,
} from '@west-australian-newspapers/publication-types'
import { sortBy, uniqBy } from 'ramda'

/**
 * @param item Either a published or killed Event Post
 * @returns True if item is a published event post
 */
export const isPublishedEventPost = (
    item: EventPostV4DTO | KilledEventPostV4DTO,
): item is EventPostV4DTO => {
    return (
        'kind' in item &&
        item.kind === 'event-post' &&
        item.state === 'published'
    )
}

/**
 * @param item Either a published or killed Event Post
 * @returns True if item is a published milestone
 */
export const isPublishedMilestone = (
    item: EventPostV4DTO | KilledEventPostV4DTO,
): item is EventPostV4DTO => {
    return (
        'kind' in item &&
        item.kind === 'event-post' &&
        item.state === 'published' &&
        item.milestone === true
    )
}

/**
 * Deduplicate Event Posts
 * @param posts Existing post array
 * @param newPosts New post array
 * @returns Deduplicated post array containing only published posts
 */
export const dedupeEventPosts = (
    posts: Array<EventPostV4DTO | KilledEventPostV4DTO>,
    newPosts: Array<EventPostV4DTO | KilledEventPostV4DTO>,
): EventPostV4DTO[] =>
    sortBy<EventPostV4DTO | KilledEventPostV4DTO>(
        (post) => toEpoch(post.publishedDate) * -1,
        uniqBy((post) => post.id, [...newPosts, ...posts]),
    ).filter(isPublishedEventPost)

export type EventIndexingSorting = Pick<EventPostV4DTO, 'id' | 'publishedDate'>

/**
 * Find the index of an event post in an array of posts
 * @param posts An array of event posts
 * @param id the ID of the post
 * @returns Index
 */
export const findIndex = <T extends EventIndexingSorting>(
    posts: Array<T>,
    id: string,
) => sortPostsDesc(posts).findIndex((a) => a.id === id)

/**
 * Sort Event Posts by publishDate
 * @param posts An array of event posts
 * @returns A array of event posts sorted by publishedDate in descending order
 */
export const sortPostsDesc = <T extends EventIndexingSorting>(
    posts: Array<T>,
): Array<T> => {
    return [...posts].sort(
        (a, b) =>
            new Date(b.publishedDate).getTime() -
            new Date(a.publishedDate).getTime(),
    )
}

/**
 * @param array An array of objects
 * @returns The last object in the array if found
 */
export const getLast = <T>(array: T[]): T | undefined => array[array.length - 1]

/**
 * Convert a date time string to epoch time
 * @param dt Date/Time string
 * @returns
 */
const toEpoch = (dt: string) => new Date(dt).getTime() / 1000
